import { imageByPath, type ImageAsset } from '@/assets';

/**
 * The agent builder's look (docs/design/agent-builder.md): layers drawn on one shared canvas
 * (art/characters/agent-builder/<layer>/<id>), stacked back to front. Tintable layers are drawn
 * in neutral light grey and multiplied by the chosen colour in the app. An item is offered only
 * when its art exists, so the builder grows as layers are added.
 */

export type LayerId = 'back' | 'body' | 'face' | 'suit' | 'shoes' | 'hair' | 'eyewear' | 'headgear';

/** Back to front. Hair over the suit; eyewear over hair (fringes stay under the goggles). */
export const LAYER_ORDER: readonly LayerId[] = [
  'back',
  'body',
  'suit',
  'shoes',
  'hair',
  'eyewear',
  'headgear',
];

export interface LookItem {
  id: string;
  /** Shown and spoken ("curly hair"); the classifier reads it. */
  label: string;
  /** Words that pick it by voice (rules). */
  says: readonly string[];
}

/** Every item per layer, in the order offered; `none` means the layer is empty. */
export const ITEMS: Readonly<Record<LayerId, readonly LookItem[]>> = {
  body: [
    { id: 'porcelain', label: 'very light', says: ['very light', 'palest', 'lightest'] },
    { id: 'light', label: 'light', says: ['light'] },
    { id: 'medium', label: 'medium', says: ['medium', 'middle'] },
    { id: 'tan', label: 'tan', says: ['tan'] },
    { id: 'brown', label: 'brown', says: ['brown'] },
    { id: 'deep', label: 'dark brown', says: ['dark brown', 'darkest', 'dark'] },
  ],
  /**
   * Face builds: the body art is drawn once per build and tone (body/<face>-<tone>), so any
   * face goes with any skin tone. Not a layer of its own.
   */
  face: [
    { id: 'a', label: 'face one', says: ['one', 'first'] },
    { id: 'b', label: 'face two', says: ['two', 'second'] },
    { id: 'c', label: 'face three', says: ['three', 'third'] },
    { id: 'd', label: 'face four', says: ['four', 'fourth'] },
    { id: 'e', label: 'face five', says: ['five', 'fifth'] },
  ],
  hair: [
    { id: 'none', label: 'no hair', says: ['no hair', 'bald', 'none'] },
    { id: 'short', label: 'short hair', says: ['short'] },
    { id: 'curls', label: 'curly hair', says: ['curly', 'curls'] },
    { id: 'long', label: 'long hair', says: ['long'] },
    { id: 'braids', label: 'braids', says: ['braids', 'braid', 'plaits'] },
    { id: 'bun', label: 'a bun', says: ['bun'] },
    { id: 'spiky', label: 'spiky hair', says: ['spiky', 'spikes'] },
    { id: 'afro', label: 'an afro', says: ['afro', 'big', 'fluffy'] },
    { id: 'ponytail', label: 'a ponytail', says: ['ponytail', 'pony', 'tied back', 'tied up'] },
    { id: 'bob', label: 'a bob', says: ['bob', 'bangs', 'fringe'] },
    { id: 'twists', label: 'twists', says: ['twists', 'locs', 'dreads'] },
  ],
  suit: [
    { id: 'stealth', label: 'stealth suit', says: ['stealth', 'ninja', 'sneaky'] },
    { id: 'tech', label: 'tech suit', says: ['tech', 'robot', 'armor', 'armour'] },
    { id: 'field', label: 'field jacket', says: ['jacket', 'field', 'explorer'] },
    { id: 'hoodie', label: 'hoodie', says: ['hoodie', 'hood'] },
    { id: 'labcoat', label: 'lab coat', says: ['lab coat', 'lab', 'coat', 'scientist'] },
  ],
  shoes: [
    { id: 'sneakers', label: 'sneakers', says: ['sneakers', 'trainers', 'runners'] },
    { id: 'boots', label: 'boots', says: ['boots', 'boot'] },
  ],
  eyewear: [
    { id: 'none', label: 'nothing', says: ['nothing', 'none', 'no'] },
    { id: 'mask', label: 'spy mask', says: ['mask'] },
    { id: 'goggles', label: 'goggles', says: ['goggles'] },
    { id: 'glasses', label: 'glasses', says: ['glasses', 'specs'] },
    { id: 'shades', label: 'sunglasses', says: ['sunglasses', 'shades', 'sun glasses'] },
  ],
  headgear: [
    { id: 'none', label: 'nothing', says: ['nothing', 'none', 'no'] },
    { id: 'cap', label: 'cap', says: ['cap', 'hat', 'baseball'] },
    { id: 'beanie', label: 'beanie', says: ['beanie', 'woolly', 'winter'] },
    { id: 'headset', label: 'headset', says: ['headset', 'headphones', 'microphone'] },
  ],
  back: [
    { id: 'none', label: 'nothing', says: ['nothing', 'none', 'no'] },
    { id: 'cape', label: 'cape', says: ['cape'] },
    { id: 'backpack', label: 'backpack', says: ['backpack', 'bag', 'rucksack'] },
    { id: 'jetpack', label: 'jetpack', says: ['jetpack', 'jet', 'rocket'] },
  ],
};

/** Layers recoloured by the child's colour choice (body is chosen by tone instead). */
export const TINTED: ReadonlySet<LayerId> = new Set([
  'suit',
  'shoes',
  'hair',
  'eyewear',
  'headgear',
  'back',
]);

export interface Colour {
  id: string;
  label: string;
  says: readonly string[];
  /** The multiply colour (CSS hex). */
  hex: string;
}

/** Natural hair colours first, then fun ones. */
export const HAIR_COLOURS: readonly Colour[] = [
  { id: 'black', label: 'black', says: ['black'], hex: '#3a3330' },
  { id: 'dark-brown', label: 'dark brown', says: ['dark brown'], hex: '#5b4033' },
  { id: 'brown', label: 'brown', says: ['brown'], hex: '#8a5d3b' },
  {
    id: 'auburn',
    label: 'auburn',
    says: ['auburn', 'red brown', 'reddish', 'reddish brown'],
    hex: '#a2512f',
  },
  { id: 'ginger', label: 'ginger', says: ['ginger', 'orange', 'red'], hex: '#d77a3c' },
  { id: 'blond', label: 'blond', says: ['blond', 'blonde', 'yellow'], hex: '#e7c27a' },
  { id: 'blue', label: 'blue', says: ['blue'], hex: '#5b8cf0' },
  { id: 'pink', label: 'pink', says: ['pink'], hex: '#f08cc0' },
];

export const GEAR_COLOURS: readonly Colour[] = [
  { id: 'black', label: 'black', says: ['black'], hex: '#4a4f5c' },
  { id: 'blue', label: 'blue', says: ['blue'], hex: '#4b7bf0' },
  { id: 'red', label: 'red', says: ['red'], hex: '#e0484c' },
  { id: 'green', label: 'green', says: ['green'], hex: '#3fae6a' },
  { id: 'purple', label: 'purple', says: ['purple'], hex: '#8a5ce0' },
  { id: 'orange', label: 'orange', says: ['orange'], hex: '#f0913a' },
  { id: 'yellow', label: 'yellow', says: ['yellow'], hex: '#f2cc3d' },
  { id: 'white', label: 'white', says: ['white'], hex: '#f4f6fa' },
];

/** The art of an item, if it exists. */
export function artFor(layer: LayerId, id: string): ImageAsset | undefined {
  return imageByPath[`characters/agent-builder/${layer}/${id}`];
}

/** The body art for a face build and skin tone (the tone alone where that build is missing). */
export function bodyArt(face: string | undefined, tone: string): ImageAsset | undefined {
  return (face ? artFor('body', `${face}-${tone}`) : undefined) ?? artFor('body', tone);
}

/** The items of a layer that can be shown (their art exists, or the layer is left empty). */
export function available(layer: LayerId): LookItem[] {
  if (layer === 'face') return ITEMS.face.filter((f) => artFor('body', `${f.id}-medium`));
  if (layer === 'body') return ITEMS.body.filter((t) => bodyArt(ITEMS.face[0]?.id, t.id));
  return ITEMS[layer].filter((i) => i.id === 'none' || artFor(layer, i.id) !== undefined);
}

/** The child's agent: an item per layer, a colour per tinted layer, and a codename. */
export interface Look {
  items: Partial<Record<LayerId, string>>;
  colours: Partial<Record<LayerId, string>>;
  codename: string | null;
}

export const DEFAULT_LOOK: Look = {
  items: { body: 'medium', face: 'a', hair: 'short', suit: 'stealth', shoes: 'sneakers' },
  colours: { hair: 'dark-brown', suit: 'blue', shoes: 'white' },
  codename: null,
};

/** A colour's hex for a layer, from the palette that layer uses. */
export function colourHex(layer: LayerId, id: string | undefined): string | undefined {
  const palette = layer === 'hair' ? HAIR_COLOURS : GEAR_COLOURS;
  return palette.find((c) => c.id === id)?.hex;
}

/** Codenames the child picks from: each with an r, easy to say, no score attached. */
export const CODENAMES: readonly string[] = [
  'Red Rocket',
  'Silver Raven',
  'Star Ranger',
  'Arctic Fox',
  'Turbo Tiger',
  'River Racer',
  'Storm Rider',
  'Mirror Wolf',
  'Thunder Bear',
  'Ruby Hawk',
  'Rapid Robin',
  'Orbit Runner',
];
